/**
 * useGrabadorAudio (hito M2 · evidencia rica).
 *
 * Grabador de notas de voz sobre MediaRecorder para el Bolsillo: el
 * participante captura audio en territorio (una asamblea, una entrevista de
 * calle) y lo sube como evidencia para transcribir. Envuelve permisos de
 * micrófono, ciclo de grabación y el blob resultante, con DEGRADACIÓN elegante
 * si el navegador no soporta la API.
 *
 * Estados:
 *   'no_soportado' -> el navegador no expone MediaRecorder/getUserMedia.
 *   'idle'         -> listo para grabar (o tras cancelar).
 *   'pidiendo'     -> esperando el permiso de micrófono.
 *   'grabando'     -> capturando; `duracionS` avanza cada segundo.
 *   'listo'        -> hay un `blob` grabado listo para subir o descartar.
 *   'error'        -> permiso denegado o fallo del dispositivo (`error` explica).
 *
 * Contrato de uso: iniciar() -> detener() deja estado 'listo' + blob; cancelar()
 * o reiniciar() vuelve a 'idle' y libera el micrófono y el blob.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  abrirGrabacion,
  guardarTrozo,
  type ContextoGrabacion,
} from '../lib/grabacionEnCurso';

/** uuid del cliente, con reserva si crypto.randomUUID no está. */
function nuevoId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return 'grab-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
}

export type EstadoGrabador =
  | 'no_soportado'
  | 'idle'
  | 'pidiendo'
  | 'grabando'
  | 'listo'
  | 'error';

export interface GrabacionAudio {
  blob: Blob;
  /** MIME real del contenedor elegido (audio/webm, audio/mp4, …). */
  mime: string;
  /** Extensión sugerida coherente con el MIME (webm/mp4/ogg…). */
  ext: string;
  /** Duración aproximada en segundos. */
  duracionS: number;
  /**
   * ISO-8601 del arranque y del final REALES de la grabación. Con la cola
   * durable un audio puede entrar al acta mucho después de grabarse: estos
   * sellos dicen cuándo se DIJO, no cuándo se pudo mandar, y ordenan la cola
   * por el momento en que se habló.
   */
  inicioISO: string;
  finISO: string;
  /**
   * Identidad de ESTA grabación. Se usa como token `idem` al enviarla, y es la
   * misma clave con la que sus trozos viven en IndexedDB. Que sean el mismo id
   * es lo que hace segura la recuperación: si la pestaña muere después de
   * enviar pero antes de limpiar, la grabación recuperada llega con el MISMO
   * idem y el backend la deduplica en vez de duplicar el aporte (C1).
   */
  id: string;
}

export interface GrabadorAudio {
  soportado: boolean;
  estado: EstadoGrabador;
  /** Segundos transcurridos de la grabación en curso o ya lista. */
  duracionS: number;
  /** Resultado disponible cuando estado === 'listo'. */
  grabacion: GrabacionAudio | null;
  /** Mensaje humano cuando estado === 'error'. */
  error: string | null;
  iniciar: () => Promise<void>;
  detener: () => void;
  /**
   * Id de la grabación en curso (o de la recién terminada). Sirve para BORRAR
   * sus trozos de IndexedDB cuando alguien cancela a propósito o cuando ya
   * pasó a la cola de envío: lo que no se limpia se recupera al reabrir.
   */
  idEnCurso: string | null;
  /** Descarta la grabación/permiso y vuelve a 'idle'. */
  reiniciar: () => void;
}

/** ¿Soporta el navegador captura + grabación de audio? */
function detectarSoporte(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    Boolean(navigator.mediaDevices?.getUserMedia) &&
    typeof window.MediaRecorder !== 'undefined'
  );
}

/** Elige el mejor contenedor soportado; devuelve {mime, ext}. */
function elegirTipo(): { mime: string; ext: string } {
  const candidatos: Array<{ mime: string; ext: string }> = [
    { mime: 'audio/webm;codecs=opus', ext: 'webm' },
    { mime: 'audio/webm', ext: 'webm' },
    { mime: 'audio/mp4', ext: 'm4a' },
    { mime: 'audio/ogg;codecs=opus', ext: 'ogg' },
  ];
  const soportaTipo =
    typeof window !== 'undefined' &&
    typeof window.MediaRecorder !== 'undefined' &&
    typeof MediaRecorder.isTypeSupported === 'function';
  if (soportaTipo) {
    for (const c of candidatos) {
      if (MediaRecorder.isTypeSupported(c.mime)) return c;
    }
  }
  // Reserva: dejamos que el navegador elija su contenedor por defecto.
  return { mime: '', ext: 'webm' };
}

/** Opciones de captura según QUÉ se está grabando. */
export interface OpcionesGrabador {
  /**
   * Procesamiento de voz del navegador (cancelación de eco, supresión de
   * ruido y control automático de ganancia).
   *
   * `true` (por defecto) para la nota de voz del participante: un celular
   * solo en la calle agradece los tres filtros.
   *
   * `false` para el MICRÓFONO DE SALA cuando entra por una consola: ahí el
   * audio ya viene mezclado y con ganancia bien puesta, y los filtros hacen
   * daño — el control automático de ganancia bombea el nivel entre silencios
   * y la supresión de ruido se come justo a quien habla bajo o lejos, que es
   * a quien más queremos rescatar.
   */
  procesarVoz?: boolean;
  /** Bitrate del contenedor; más alto para grabaciones largas de sala. */
  bitsPorSegundo?: number;
  /**
   * PERSISTENCIA POR TROZOS. Sin esto, `MediaRecorder` acumula todo en la
   * memoria de la pestaña y el audio solo existe al DETENER: una grabación de
   * sala de dos horas se pierde entera si la laptop se suspende o el navegador
   * mata la pestaña. Con esto, cada trozo se escribe a IndexedDB apenas llega
   * y al reabrir la app se reensambla lo grabado; se pierde a lo sumo el
   * último trozo, nunca la jornada.
   */
  persistir?: {
    actividadId: string;
    contexto: ContextoGrabacion;
    microfonoSala?: boolean;
    numHablantes?: number;
    campos?: Record<string, unknown>;
    /** Segundos por trozo (por defecto 5: el máximo que se puede perder). */
    segundos?: number;
  } | null;
}

export function useGrabadorAudio(opciones: OpcionesGrabador = {}): GrabadorAudio {
  const { procesarVoz = true, bitsPorSegundo, persistir } = opciones;
  const soportado = detectarSoporte();
  const [estado, setEstado] = useState<EstadoGrabador>(soportado ? 'idle' : 'no_soportado');
  const [duracionS, setDuracionS] = useState(0);
  const [grabacion, setGrabacion] = useState<GrabacionAudio | null>(null);
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const tipoRef = useRef<{ mime: string; ext: string }>({ mime: '', ext: 'webm' });
  const inicioRef = useRef<number>(0);
  const tickRef = useRef<number | null>(null);
  /** Identidad de la grabación en curso (= idem del envío y clave de sus trozos). */
  const idRef = useRef<string>('');
  const trozoRef = useRef<number>(0);
  // El `persistir` se lee dentro de callbacks del MediaRecorder: por ref para
  // no recrear `iniciar` (y perder la grabación) en cada render del padre.
  const persistirRef = useRef(persistir);
  persistirRef.current = persistir;

  const pararTick = useCallback(() => {
    if (tickRef.current !== null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  const soltarStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const iniciar = useCallback(async () => {
    if (!soportado) return;
    setError(null);
    setGrabacion(null);
    setDuracionS(0);
    setEstado('pidiendo');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: procesarVoz
          ? true
          : {
              echoCancellation: false,
              noiseSuppression: false,
              autoGainControl: false,
            },
      });
      streamRef.current = stream;
      const tipo = elegirTipo();
      tipoRef.current = tipo;
      const ajustes: MediaRecorderOptions = {};
      if (tipo.mime) ajustes.mimeType = tipo.mime;
      if (bitsPorSegundo) ajustes.audioBitsPerSecond = bitsPorSegundo;
      const rec = Object.keys(ajustes).length
        ? new MediaRecorder(stream, ajustes)
        : new MediaRecorder(stream);
      recorderRef.current = rec;
      chunksRef.current = [];

      rec.ondataavailable = (e: BlobEvent) => {
        if (!e.data || e.data.size === 0) return;
        chunksRef.current.push(e.data);
        // ESCRITURA INMEDIATA: desde acá, este trozo ya sobrevive a que la
        // pestaña muera o la máquina se suspenda.
        if (persistirRef.current) {
          const indice = trozoRef.current;
          trozoRef.current += 1;
          void guardarTrozo(idRef.current, indice, e.data).catch(() => {
            /* sin almacén: la grabación en memoria sigue siendo válida */
          });
        }
      };
      rec.onstop = () => {
        pararTick();
        const mime = rec.mimeType || tipoRef.current.mime || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: mime });
        const dur = Math.max(1, Math.round((Date.now() - inicioRef.current) / 1000));
        soltarStream();
        if (blob.size === 0) {
          setError('No se capturó audio. Intentá de nuevo.');
          setEstado('error');
          return;
        }
        setGrabacion({
          blob,
          mime,
          ext: tipoRef.current.ext,
          duracionS: dur,
          inicioISO: new Date(inicioRef.current).toISOString(),
          finISO: new Date().toISOString(),
          id: idRef.current,
        });
        setDuracionS(dur);
        setEstado('listo');
      };

      inicioRef.current = Date.now();
      idRef.current = nuevoId();
      trozoRef.current = 0;

      const cfg = persistirRef.current;
      if (cfg) {
        const segundos = cfg.segundos ?? 5;
        // El expediente se abre ANTES del primer trozo: si la pestaña muere a
        // los 3 segundos, ya hay de qué recuperar.
        void abrirGrabacion({
          id: idRef.current,
          contexto: cfg.contexto,
          actividadId: cfg.actividadId,
          mime: rec.mimeType || tipo.mime || 'audio/webm',
          ext: tipo.ext,
          inicio: new Date(inicioRef.current).toISOString(),
          ultimoTrozo: new Date(inicioRef.current).toISOString(),
          trozos: 0,
          segundosPorTrozo: segundos,
          microfonoSala: cfg.microfonoSala,
          numHablantes: cfg.numHablantes,
          campos: cfg.campos,
        }).catch(() => {
          /* sin almacén: se graba igual, solo sin red de seguridad */
        });
        // timeslice: entrega un trozo cada N segundos en vez de todo al final.
        rec.start(segundos * 1000);
      } else {
        rec.start();
      }
      setEstado('grabando');
      pararTick();
      tickRef.current = window.setInterval(() => {
        setDuracionS(Math.round((Date.now() - inicioRef.current) / 1000));
      }, 1000);
    } catch (err) {
      soltarStream();
      const name = err && typeof err === 'object' ? (err as { name?: string }).name : undefined;
      setError(
        name === 'NotAllowedError' || name === 'SecurityError'
          ? 'Necesitamos permiso para usar el micrófono. Activalo y volvé a intentar.'
          : name === 'NotFoundError'
            ? 'No encontramos un micrófono en este dispositivo.'
            : 'No pudimos iniciar la grabación. Intentá de nuevo.',
      );
      setEstado('error');
    }
  }, [soportado, pararTick, soltarStream, procesarVoz, bitsPorSegundo]);

  const detener = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      try {
        rec.stop();
      } catch {
        /* noop: onstop no correrá; limpiamos abajo */
        pararTick();
        soltarStream();
        setEstado('idle');
      }
    }
  }, [pararTick, soltarStream]);

  const reiniciar = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      rec.onstop = null;
      try {
        rec.stop();
      } catch {
        /* noop */
      }
    }
    recorderRef.current = null;
    pararTick();
    soltarStream();
    chunksRef.current = [];
    setGrabacion(null);
    setDuracionS(0);
    setError(null);
    setEstado('idle');
  }, [pararTick, soltarStream]);

  // Limpieza dura al desmontar: nunca dejar el micrófono abierto.
  useEffect(() => {
    return () => {
      pararTick();
      const rec = recorderRef.current;
      if (rec && rec.state !== 'inactive') {
        rec.onstop = null;
        try {
          rec.stop();
        } catch {
          /* noop */
        }
      }
      soltarStream();
    };
  }, [pararTick, soltarStream]);

  return {
    soportado,
    estado,
    duracionS,
    grabacion,
    error,
    iniciar,
    detener,
    reiniciar,
    idEnCurso: idRef.current || null,
  };
}

/** Formatea segundos como m:ss para el cronómetro del grabador. */
export function formatearDuracion(totalS: number): string {
  const s = Math.max(0, Math.floor(totalS));
  const min = Math.floor(s / 60);
  const seg = s % 60;
  return `${min}:${seg.toString().padStart(2, '0')}`;
}
